import { ORGANIZATIONS_V2 } from 'constants/organizationServiceV2Flags';
import { isFlagEnabled } from './launchDarkly';
import { getSelectedOrganizationId } from './selectedOrganization';
import { CROPWISE_PROXY_URL, CROPWISE_PROXY_V2_URL } from './apiConstants';

/**
 * Routing helpers for the Organization Service V2 (Java -> Node) migration.
 *
 * Every helper resolves the backend when it is called, not when it is imported, so
 * the axios resources in `utilities/api` can be built once at module load and still
 * send each request to whichever backend `releaseBeOrganizationsV2` currently selects.
 */

/** True when organization requests should go to the Node service. */
export const isOrganizationsV2Enabled = (): boolean =>
  isFlagEnabled(ORGANIZATIONS_V2);

/**
 * Java uses the singular `organization` for every operation except the list; Node
 * uses the plural `organizations` for all of them.
 */
export const ORGANIZATION_V2_RESOURCE_NAME = 'organizations';

/**
 * Node's single-organization operations are reached through actions rather than a
 * resource name, so the resource can keep Java's `organization` name. `helpers/
 * resourceLookup` is keyed by that name, and `organization` vs `organizations` selects
 * two different caching policies - renaming it would make the detail read Dexie-cached
 * and stop organization writes from clearing that cache.
 */
const ORGANIZATION_V2_ROOT_ACTION = 'organizations';
const ORGANIZATION_V2_DETAIL_ACTION = 'organizations/detail';

/**
 * Ordered, first match wins: the Java paths share prefixes and Node does not rename
 * them uniformly. `member/invitation` loses its `member` parent and becomes plain
 * `invitation`, while `member/invite` keeps it as `members/invite` - so the longer
 * pattern has to be tried before the shorter one it would otherwise shadow.
 */
const ACTION_REWRITES: ReadonlyArray<readonly [RegExp, string]> = [
  [/^organization\/member\/invitation(?=\/|$)/, 'organizations/invitation'],
  [/^organization\/member\/invite(?=\/|$)/, 'organizations/members/invite'],
  [/^organization\/member(?=\/|$)/, 'organizations/members'],
  [/^organization\/account(?=\/|$)/, 'organizations/accounts'],
  [/^organization(?=\/|$)/, 'organizations']
];

/**
 * Rewrites a `createChildApi` action from the Java path to the Node path, e.g.
 * `organization/account/{id}/revoke` -> `organizations/accounts/{id}/revoke`.
 * Idempotent: an action already using the plural form is returned unchanged.
 */
export const remapOrganizationAction = (action: string): string => {
  const rewrite = ACTION_REWRITES.find(([pattern]) => pattern.test(action));
  return rewrite ? action.replace(rewrite[0], rewrite[1]) : action;
};

/**
 * Paths with no Node counterpart, which stay on Java even while the flag is on.
 *
 * Only the `/revoke` form of the account delete was ported. `organization/account/{id}`
 * on its own deletes an already-revoked member from the list and still goes to Java,
 * so routing it to Node would only 404.
 *
 * `organization/land-management-enabled` is Java-only as well, but it never reaches
 * this seam - `utilities/api` builds it as its own `LMOrganization` resource.
 */
const JAVA_ONLY_ACTIONS = [/^organization\/account\/[^/]+$/];

export const isJavaOnlyOrganizationAction = (action?: string): boolean =>
  !!action && JAVA_ONLY_ACTIONS.some(pattern => pattern.test(action));

interface OrganizationResource {
  fetch: (...args: unknown[]) => unknown;
  update: (...args: unknown[]) => unknown;
  delete: (...args: unknown[]) => unknown;
  createChildApi: (options: Record<string, unknown>) => OrganizationResource;
}

/**
 * Stands in for the `{ promise, cancel }` an api function would have returned, so a
 * request we refuse to send still reaches the caller's `.catch` chain. Throwing here
 * instead would fire before the caller destructures the result and skip that chain
 * entirely, leaving the error unreported and any saving state stuck on.
 */
const refusedRequest = (message: string) => ({
  promise: Promise.reject(new Error(message)),
  cancel: () => {}
});

/**
 * Adapts the Node organization resource to the signatures Java callers already use,
 * so the two backends stay interchangeable behind `makeRoutedResource` and no call
 * site has to know which one it is talking to:
 *
 * - every operation sits under `organizations`, and the read has its own
 *   `organizations/detail` path rather than the root;
 * - update and delete are `organizations/{id}`, where Java reads the organization
 *   from the `cwf-context` header and takes no id at all. Callers pass none, so the
 *   selected organization - the one that header would have named - is filled in here.
 *
 * Child apis are deliberately left on the base resource: their paths come from an
 * action that `remapOrganizationAction` has already rewritten.
 */
export const adaptOrganizationNodeApi = <T>(nodeApi: T): T => {
  const resource = (nodeApi as unknown) as OrganizationResource;
  const root = resource.createChildApi({
    action: ORGANIZATION_V2_ROOT_ACTION
  });
  const detail = resource.createChildApi({
    action: ORGANIZATION_V2_DETAIL_ACTION
  });
  // The selection is null whenever the browser denies storage, a case Java never felt
  // because its header is held in memory. Left to run, that null would drop the id
  // segment and turn a single-organization update or delete into a request against
  // the whole collection.
  const forSelectedOrg = (
    operation: string,
    method: (...args: unknown[]) => unknown
  ) => (id: unknown, ...rest: unknown[]) => {
    const organizationId = id ?? getSelectedOrganizationId();
    if (!organizationId) {
      return refusedRequest(
        `Cannot ${operation} an organization without a selected organization id`
      );
    }
    return method(organizationId, ...rest);
  };

  return ({
    ...root,
    createChildApi: (options: Record<string, unknown>) =>
      resource.createChildApi(options),
    fetch: (...args: unknown[]) => detail.fetch(...args),
    update: forSelectedOrg('update', (...args) => root.update(...args)),
    delete: forSelectedOrg('delete', (...args) => root.delete(...args))
  } as unknown) as T;
};

/** Organization list endpoint, for the callers that use raw axios instead of the resources. */
export const getOrganizationsUrl = (): string =>
  isOrganizationsV2Enabled()
    ? `${CROPWISE_PROXY_V2_URL}/organizations`
    : `${CROPWISE_PROXY_URL}/v1/base/organizations`;

/** Single-organization endpoint, for the callers that use raw axios instead of the resources. */
export const getOrganizationDetailUrl = (): string =>
  isOrganizationsV2Enabled()
    ? `${CROPWISE_PROXY_V2_URL}/${ORGANIZATION_V2_DETAIL_ACTION}`
    : `${CROPWISE_PROXY_URL}/v1/base/organization`;
